import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  ChangeRequestItem,
  ImpactedStore,
  StoreAddressContactChangeApprovalData,
  StoreChangeRequestKind,
  StoreChangeRequestResolutionSummary,
  StoreChangeRequestResponse,
  StoreChangeRequestType,
  StoreHoursChangeApprovalData,
  StoreHoursChangeApprovalDayRow
} from "@ubif/types";
import { formatDateAsMMDDYYY, formatStoreWithIdLabel } from "@ubif/util-store";
import { TFunction } from "i18next";

export interface MappedStoreChangeRequest {
  requestType: StoreChangeRequestType;
  addressData?: StoreAddressContactChangeApprovalData;
  businessHoursData?: StoreHoursChangeApprovalData;
  temporaryClosureData?: StoreHoursChangeApprovalData;
  resolutionSummary?: StoreChangeRequestResolutionSummary;
}

export function mapStoreChangeRequest(
  response: StoreChangeRequestResponse,
  reasonDropdownOptions: { name: string; value: string }[]
): MappedStoreChangeRequest | null {
  const resolutionSummary = toResolutionSummary(response);

  switch (response.type) {
    case StoreChangeRequestKind.STORE_DETAILS_UPDATE:
      return {
        requestType: "address",
        addressData: toAddressData(response),
        ...(resolutionSummary ? { resolutionSummary } : {})
      };
    case StoreChangeRequestKind.BUSINESS_HOURS_UPDATE:
      return {
        requestType: "businessHours",
        businessHoursData: toHoursData(response, "businessHours", reasonDropdownOptions),
        ...(resolutionSummary ? { resolutionSummary } : {})
      };
    case StoreChangeRequestKind.BUSINESS_HOURS_TEMP_CLOSURE:
      return {
        requestType: "temporaryClosure",
        temporaryClosureData: toHoursData(response, "temporaryClosure", reasonDropdownOptions),
        ...(resolutionSummary ? { resolutionSummary } : {})
      };
    default:
      return null;
  }
}

function toResolutionSummary(response: StoreChangeRequestResponse): StoreChangeRequestResolutionSummary | undefined {
  const status = (response.status ?? "").trim().toLowerCase();
  if (status !== "approved" && status !== "declined" && status !== "rejected") {
    return undefined;
  }
  const outcome = status === "approved" ? "approved" : "declined";
  const approvedAction = response.actions.find((a) => a.action === "approved");
  const declinedAction = response.actions.find((a) => a.action === "declined");
  const actorDisplayName = outcome === "approved" ? (approvedAction?.user ?? "") : (declinedAction?.user ?? "");
  const resolutionDateIso = outcome === "approved" ? approvedAction?.date : declinedAction?.date;
  const trimmedReason = declinedAction?.note?.trim();
  return {
    outcome,
    actorDisplayName,
    dateDisplay: formatIsoDate(resolutionDateIso),
    ...(outcome === "declined" && trimmedReason ? { declineReason: trimmedReason } : {})
  };
}

function toAddressData(response: StoreChangeRequestResponse): StoreAddressContactChangeApprovalData {
  const phoneItem = findItem(response.changeRequest, "phone");
  const addressItem = findItem(response.changeRequest, "address");

  const requested = response.actions.find((a) => a.action === "requested");
  return {
    requestorName: requested?.user ?? "",
    requestedDate: formatIsoDate(requested?.date),
    impactedStore: joinStoreNames(response.impactedStores),
    originalPhoneNumber: phoneItem?.original ?? null,
    newPhoneNumber: phoneItem?.newValue ?? null,
    originalAddress: addressItem?.original ?? null,
    newAddress: addressItem?.newValue ?? null,
    effectiveDate: formatIsoDate(`${response.effectiveDate.split("T")[0]} 00:00`),
    expirationDate: response.expirationDate ? formatIsoDate(`${response.expirationDate.split("T")[0]} 00:00`) : null,
    note: response.note ?? null,
    showPhoneNumberSection: Boolean(phoneItem),
    showAddressSection: Boolean(addressItem),
    status: response.status
  };
}

function toHoursData(
  response: StoreChangeRequestResponse,
  requestType: "businessHours" | "temporaryClosure",
  reasonDropdownOptions: { name: string; value: string }[]
): StoreHoursChangeApprovalData {
  const isClosure = requestType === "temporaryClosure";
  const effectiveSource = isClosure
    ? (response.temporaryClosureStart ?? response.effectiveDate)
    : response.effectiveDate;
  const expirationSource = isClosure
    ? (response.temporaryClosureEnd ?? response.expirationDate)
    : response.expirationDate;

  const requested = response.actions.find((a) => a.action === "requested");
  const reasonForChangeOption = reasonDropdownOptions.find((option) => option.value === response.reasonForChange);
  return {
    requestType,
    requestorName: requested?.user ?? "",
    status: response.status,
    requestedDate: formatIsoDate(requested?.date),
    impactedStores: response.impactedStores
      .map((store) => (store.name ? formatStoreWithIdLabel(store.name, store.storeId) : null))
      .filter((name): name is string => Boolean(name)),
    dayRows: buildDayRows(response.changeRequest),
    effectiveDate: formatIsoDate(`${effectiveSource.split("T")[0]} 00:00`),
    expirationDate: expirationSource ? formatIsoDate(`${expirationSource.split("T")[0]} 00:00`) : null,
    reasonForChange: reasonForChangeOption?.name ?? response.reasonForChange ?? "",
    note: response.note ?? null
  };
}

function findItem(items: ChangeRequestItem[], field: ChangeRequestItem["field"]): ChangeRequestItem | undefined {
  return items.find((item) => item.field === field);
}

function buildDayRows(items: ChangeRequestItem[]): StoreHoursChangeApprovalDayRow[] {
  return items
    .filter((item) => item.field === "hours" && typeof item.dayOfWeek === "number")
    .slice()
    .sort((a, b) => (a.dayOfWeek ?? 0) - (b.dayOfWeek ?? 0))
    .map((item) => ({
      dayOfWeek: item.dayOfWeek ?? 0,
      originalHours: item.original ?? "",
      newHours: item.newValue ?? ""
    }));
}

function joinStoreNames(stores: ImpactedStore[]): string {
  return stores
    .map((store) => (store.name ? formatStoreWithIdLabel(store.name, store.storeId) : null))
    .filter((name): name is string => Boolean(name))
    .join(", ");
}

function formatIsoDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDateAsMMDDYYY(date);
}

/** Narrows RTK Query `error` (fetch vs serialized) for safe access to HTTP `status`. */
export function isFetchBaseQueryError(
  error: FetchBaseQueryError | SerializedError | undefined | null
): error is FetchBaseQueryError {
  return typeof error === "object" && error != null && "status" in error;
}

/** Dropdown options for the store change reason field (names translated). */
export function getStoreChangeReasonDropdownOptions(
  t: TFunction<"root-config", undefined>
): { name: string; value: string }[] {
  return [
    { value: "acquisition", name: t("MyStore.StoreChangeReasonAcquisition") },
    { value: "short_staff", name: t("MyStore.StoreChangeReasonShortStaff") },
    { value: "no_staff", name: t("MyStore.StoreChangeReasonNoStaff") },
    { value: "building_facility_issue", name: t("MyStore.StoreChangeReasonBuildingFacilityIssue") },
    { value: "weather_act_of_god", name: t("MyStore.StoreChangeReasonWeatherActOfGod") },
    { value: "return_to_normal_hours", name: t("MyStore.StoreChangeReasonReturnToNormalHours") },
    { value: "power_outage", name: t("MyStore.StoreChangeReasonPowerOutage") },
    { value: "holiday", name: t("MyStore.StoreChangeReasonHoliday") },
    { value: "other", name: t("MyStore.StoreChangeReasonOther") }
  ];
}
